/* Epic Braceletz — bundle checkout linking
   Runs on every page (loaded from theme.liquid) because the cart
   drawer lives in the header and the cart page is its own route —
   a bundle can be edited from either place.

   How it works: bundle line items get two hidden properties when
   they're added to the cart (see braceletz-bundles.js):
     _bundle_id   — shared by every item in that one bundle instance
     _bundle_size — how many items that bundle instance should have
   (properties starting with an underscore are automatically hidden
   from the customer-facing cart/checkout).

   This re-reads the current cart on every relevant cart action,
   groups bundle-tagged items by _bundle_id, and removes any group
   that isn't at its full expected size — which is what guarantees a
   customer can never end up checking out with a lone leftover
   bracelet, no matter which cart surface the deletion happened from.

   Two implementation notes worth knowing if this needs touching again:
   - Removals are done ONE AT A TIME on purpose, not in parallel.
     Firing several /cart/change requests at once risked a lost-update
     race (one write silently clobbering another), which is what was
     leaving a stray item behind before. Sequential is slightly slower
     but doesn't lose writes.
   - After a cleanup pass, it re-checks the result and retries (capped
     at a few attempts) in case a request above had a hiccup — this is
     the actual guarantee against "checkout with a partial bundle",
     not just the first pass.

   Ordinary (untagged) line items are never touched.
*/

(function () {
  'use strict';

  if (window.__epicBzBundleLinkingInstalled) return;
  window.__epicBzBundleLinkingInstalled = true;

  var originalFetch = window.fetch.bind(window);

  // /cart/change.js and /cart/update.js return the complete current
  // cart in their response, so that can be used directly.
  var FULL_STATE_PATTERNS = ['/cart/change', '/cart/update'];
  // /cart/add.js only returns the newly-added items (not the full
  // cart), and /cart/clear.js needs a fresh read too — a plain GET
  // /cart.js is used for these instead.
  var PARTIAL_STATE_PATTERNS = ['/cart/add', '/cart/clear'];

  var queue = Promise.resolve();

  function matches(url, patterns) {
    if (!url) return false;
    var path = typeof url === 'string' ? url : (url.url || '');
    for (var i = 0; i < patterns.length; i++) {
      if (path.indexOf(patterns[i]) !== -1) return true;
    }
    return false;
  }

  function fetchCartState() {
    return originalFetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function getRefreshTargets() {
    var targets = [];
    if (document.querySelector('cart-drawer')) {
      targets.push({ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' });
    }
    if (document.getElementById('cart-icon-bubble')) {
      targets.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    }
    var mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems && mainCartItems.dataset.id) {
      targets.push({ id: 'main-cart-items', section: mainCartItems.dataset.id, selector: '.js-contents' });
    }
    var mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter && mainCartFooter.dataset.id) {
      targets.push({ id: 'main-cart-footer', section: mainCartFooter.dataset.id, selector: '.js-contents' });
    }
    return targets;
  }

  function applySections(sections, targets) {
    if (!sections) return;
    targets.forEach(function (target) {
      var html = sections[target.section];
      if (!html) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var sourceEl = parsed.querySelector(target.selector);
      var containerEl = document.getElementById(target.id);
      if (!containerEl) return;
      var destEl = containerEl.querySelector(target.selector) || containerEl;
      destEl.innerHTML = sourceEl ? sourceEl.innerHTML : (parsed.body ? parsed.body.innerHTML : '');
    });
  }

  // Targets a specific cart line by its unique KEY (not numeric
  // position), so removing several lines in a row can't be thrown off
  // by earlier removals shifting everyone else's position.
  function removeLineByKey(key, targets) {
    var body = JSON.stringify({
      id: key,
      quantity: 0,
      sections: targets.map(function (t) { return t.section; }),
      sections_url: window.location.pathname
    });
    return originalFetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body
    }).then(function (r) { return r.json(); });
  }

  function findIncompleteGroupKeys(cart) {
    var groups = {};
    (cart.items || []).forEach(function (item) {
      var props = item.properties || {};
      var bundleId = props._bundle_id;
      if (!bundleId) return;
      if (!groups[bundleId]) groups[bundleId] = [];
      groups[bundleId].push(item);
    });

    var keys = [];
    Object.keys(groups).forEach(function (bundleId) {
      var group = groups[bundleId];
      var expected = parseInt((group[0].properties || {})._bundle_size, 10);
      if (!expected || group.length >= expected) return;
      group.forEach(function (item) { keys.push(item.key); });
    });
    return keys;
  }

  function removeKeysSequentially(keys, targets) {
    var seq = Promise.resolve();
    var last = null;
    keys.forEach(function (key) {
      seq = seq.then(function () {
        return removeLineByKey(key, targets).then(function (cart) {
          last = cart;
        });
      });
    });
    return seq.then(function () { return last; });
  }

  function healCart(cart, attempt) {
    attempt = attempt || 1;
    var toRemove = findIncompleteGroupKeys(cart);
    if (toRemove.length === 0) return Promise.resolve();

    var targets = getRefreshTargets();
    return removeKeysSequentially(toRemove, targets).then(function (finalCart) {
      if (!finalCart) return;
      applySections(finalCart.sections, targets);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'epic-braceletz-bundle-linking' });
      }
      if (attempt < 4 && findIncompleteGroupKeys(finalCart).length > 0) {
        return healCart(finalCart, attempt + 1);
      }
    });
  }

  function auditFromFreshState() {
    return fetchCartState().then(healCart).catch(function (err) {
      console.error('Epic Braceletz bundle linking: audit failed.', err);
    });
  }

  function auditFromResponse(cart) {
    if (!cart || !cart.items) return auditFromFreshState();
    return healCart(cart).catch(function (err) {
      console.error('Epic Braceletz bundle linking: audit failed.', err);
    });
  }

  // Audit once on page load too, in case a customer arrives with an
  // already-incomplete bundle left over from an earlier session — but
  // skip the network round trip entirely when Dawn's own server-rendered
  // markup already tells us the cart is empty (no incomplete bundle is
  // possible then), which is the common case for most page views.
  // requestIdleCallback (where supported) keeps this off the critical
  // path for anything actually rendering the page.
  var cartIsEmpty = !document.querySelector('.cart-count-bubble');
  if (!cartIsEmpty) {
    var runInitialAudit = function () {
      queue = queue.then(auditFromFreshState);
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(runInitialAudit, { timeout: 2000 });
    } else {
      setTimeout(runInitialAudit, 0);
    }
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url);
    var isFullState = matches(url, FULL_STATE_PATTERNS);
    var isPartialState = matches(url, PARTIAL_STATE_PATTERNS);
    var promise = originalFetch(input, init);

    if (!isFullState && !isPartialState) return promise;

    return promise.then(function (response) {
      if (isFullState) {
        var clone = response.clone();
        queue = queue.then(function () {
          return clone.json().then(auditFromResponse).catch(function () {
            /* non-JSON or error response on a cart endpoint — ignore */
          });
        });
      } else {
        queue = queue.then(auditFromFreshState);
      }
      return response;
    });
  };
})();
